<script lang="ts">
  import BottomSheet from './BottomSheet.svelte';
  import type { Roulette, RouletteId } from '../lib/types';

  interface Props {
    open: boolean;
    roulettes: Roulette[];
    activeId: RouletteId;
    onselect: (id: RouletteId) => void;
    onadd: (name: string) => void;
    onrename: (id: RouletteId, name: string) => void;
    ondelete: (id: RouletteId) => void;
    onhelp: () => void;
    onhistory: () => void;
    onappsettings: () => void;
    onvisibleToggle: (id: RouletteId, visible: boolean) => void;
    onduplicate: (id: RouletteId) => void;
    onclose: () => void;
  }

  let {
    open,
    roulettes,
    activeId,
    onselect,
    onadd,
    onrename,
    ondelete,
    onhelp,
    onhistory,
    onappsettings,
    onvisibleToggle,
    onduplicate,
    onclose,
  }: Props = $props();

  let editingId = $state<RouletteId | null>(null);
  let editingName = $state('');
  let showAdd = $state(false);
  let newName = $state('');
  let confirmDeleteId = $state<RouletteId | null>(null);

  $effect(() => {
    if (!open) {
      editingId = null;
      editingName = '';
      showAdd = false;
      newName = '';
      confirmDeleteId = null;
    }
  });

  function startRename(r: Roulette): void {
    editingId = r.id;
    editingName = r.name;
  }

  function commitRename(): void {
    if (editingId && editingName.trim()) {
      onrename(editingId, editingName.trim());
    }
    editingId = null;
    editingName = '';
  }

  function cancelRename(): void {
    editingId = null;
    editingName = '';
  }

  function commitAdd(): void {
    const name = newName.trim();
    if (name) {
      onadd(name);
      onclose();
    }
    newName = '';
    showAdd = false;
  }

  function handleSelect(id: RouletteId): void {
    onselect(id);
    onclose();
  }

  function requestDelete(r: Roulette): void {
    if (roulettes.length <= 1) return;
    confirmDeleteId = r.id;
  }

  function confirmDelete(): void {
    if (confirmDeleteId) {
      ondelete(confirmDeleteId);
      confirmDeleteId = null;
    }
  }

  function totalItems(r: Roulette): number {
    return r.sets.reduce((s, set) => s + set.items.length, 0);
  }
</script>

<BottomSheet {open} title="ルーレット" {onclose}>
  <ul class="rl-list">
    {#each roulettes as r (r.id)}
      <li class="rl-row" class:active={r.id === activeId}>
        {#if editingId === r.id}
          <input
            class="rename-input"
            bind:value={editingName}
            onkeydown={(e) => {
              if (e.key === 'Enter') commitRename();
              else if (e.key === 'Escape') cancelRename();
            }}
            onblur={commitRename}
          />
        {:else if confirmDeleteId === r.id}
          <span class="confirm-text">削除しますか？</span>
          <button class="confirm-no" onclick={() => (confirmDeleteId = null)}>
            キャンセル
          </button>
          <button class="confirm-yes" onclick={confirmDelete}>削除</button>
        {:else}
          <button class="rl-select" onclick={() => handleSelect(r.id)}>
            <span class="check">{r.id === activeId ? '●' : '○'}</span>
            <span class="rl-name">{r.name}</span>
            <span class="meta">{r.sets.length}セット / {totalItems(r)}項目</span>
          </button>
          <button
            class="action-btn vis"
            class:on={r.visible}
            onclick={() => onvisibleToggle(r.id, !r.visible)}
            aria-label={r.visible ? '非表示にする' : '表示する'}
            title={r.visible ? 'グリッドに表示中 (タップで非表示)' : 'グリッド非表示 (タップで表示)'}
          >
            {r.visible ? '👁' : '⊘'}
          </button>
          <button class="action-btn" onclick={() => startRename(r)} aria-label="改名">✎</button>
          <button class="action-btn" onclick={() => onduplicate(r.id)} aria-label="複製" title="複製">⎘</button>
          <button
            class="action-btn delete"
            onclick={() => requestDelete(r)}
            disabled={roulettes.length <= 1}
            aria-label="削除"
          >🗑</button>
        {/if}
      </li>
    {/each}
  </ul>

  {#if showAdd}
    <div class="add-form">
      <input
        class="add-input"
        bind:value={newName}
        placeholder="新しいルーレット名"
        onkeydown={(e) => {
          if (e.key === 'Enter') commitAdd();
          else if (e.key === 'Escape') {
            showAdd = false;
            newName = '';
          }
        }}
      />
      <button class="primary" onclick={commitAdd} disabled={!newName.trim()}>追加</button>
      <button class="cancel" onclick={() => { showAdd = false; newName = ''; }}>取消</button>
    </div>
  {:else}
    <button class="add-btn" onclick={() => (showAdd = true)}>＋ 新規ルーレット</button>
  {/if}

  <div class="footer">
    <button class="link-btn" onclick={() => { onclose(); onhistory(); }}>
      履歴 (現在のルーレット)
    </button>
    <button class="link-btn" onclick={() => { onclose(); onappsettings(); }}>
      アプリ設定
    </button>
    <button class="link-btn" onclick={() => { onclose(); onhelp(); }}>ヘルプ</button>
  </div>
</BottomSheet>

<style>
  .rl-list {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .rl-row {
    display: flex;
    align-items: center;
    gap: 0;
    border-bottom: 1px solid var(--color-border);
    min-height: 56px;
    padding: 0 4px;
  }
  .rl-row.active {
    background: rgba(233, 69, 96, 0.07);
  }
  .rl-select {
    flex: 1;
    display: flex;
    align-items: center;
    gap: var(--space-sm);
    padding: 12px var(--space-sm);
    text-align: left;
    min-width: 0;
  }
  .check {
    color: var(--color-accent);
    width: 16px;
    text-align: center;
    flex-shrink: 0;
  }
  .rl-name {
    flex: 1;
    font-size: var(--font-size-base);
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .meta {
    font-size: 11px;
    color: var(--color-fg-muted);
    background: var(--color-border);
    padding: 2px 8px;
    border-radius: 100px;
    flex-shrink: 0;
  }
  .action-btn {
    width: 26px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-fg-muted);
    border-radius: 4px;
    flex-shrink: 0;
    font-size: 13px;
  }
  .action-btn:active:not(:disabled) {
    background: var(--color-border);
  }
  .action-btn:disabled {
    opacity: 0.25;
  }
  .action-btn.vis {
    opacity: 0.4;
  }
  .action-btn.vis.on {
    opacity: 1;
    color: var(--color-accent);
  }
  .rename-input {
    flex: 1;
    padding: 10px;
    border: 2px solid var(--color-accent);
    border-radius: var(--radius-sm);
    background: var(--color-bg);
    color: var(--color-fg);
    font-size: var(--font-size-base);
    margin: 6px 0;
  }
  .confirm-text {
    flex: 1;
    font-size: var(--font-size-sm);
    color: var(--color-accent);
    font-weight: 600;
    padding-left: var(--space-sm);
  }
  .confirm-no, .confirm-yes {
    padding: 8px 12px;
    border-radius: var(--radius-sm);
    font-size: var(--font-size-sm);
    font-weight: 600;
  }
  .confirm-no {
    background: var(--color-border);
    color: var(--color-fg);
  }
  .confirm-yes {
    background: var(--color-accent);
    color: var(--color-accent-fg);
  }
  .add-btn {
    width: 100%;
    padding: 14px;
    margin-top: var(--space-md);
    border: 1px dashed var(--color-border);
    border-radius: var(--radius-sm);
    color: var(--color-fg-muted);
    font-weight: 500;
  }
  .add-btn:active {
    background: var(--color-border);
  }
  .add-form {
    display: flex;
    gap: var(--space-sm);
    margin-top: var(--space-md);
    flex-wrap: wrap;
  }
  .add-input {
    flex: 1 1 100%;
    padding: 10px;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
    background: var(--color-bg);
    color: var(--color-fg);
  }
  .add-form button {
    flex: 1;
    padding: 10px var(--space-md);
    border-radius: var(--radius-sm);
    font-weight: 600;
    font-size: var(--font-size-sm);
  }
  .add-form .primary {
    background: var(--color-accent);
    color: var(--color-accent-fg);
  }
  .add-form .primary:disabled {
    opacity: 0.4;
  }
  .add-form .cancel {
    background: var(--color-border);
    color: var(--color-fg);
  }
  .footer {
    margin-top: var(--space-lg);
    padding-top: var(--space-md);
    border-top: 1px solid var(--color-border);
    text-align: center;
  }
  .link-btn {
    padding: 8px 16px;
    color: var(--color-fg-muted);
    font-size: var(--font-size-sm);
    border-radius: var(--radius-sm);
  }
  .link-btn:active {
    background: var(--color-border);
  }
</style>
